import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { Form, Formik } from 'formik';
import Yup from 'utils/validation/yup-extensions';
import RequiredIndicator from 'pages/ApplicationRequest/components/containers/RequiredIndicator';
import {
	getTrainingTrack,
	searchTrainingTracks,
	submitTrainingTrackForm,
} from '../_api';
import { TrainingTrackDTO } from '../_classes';
import { BEM_NAME_ELEMENTS, TRAINING_TRACK_KEYS } from '../_const';
import FormControlCustomized from '../layouts/FormControlCustomized';

/**
 * Create or update a Training Track
 * @param {object} trainingTrack training track details (empty if new)
 * @param {boolean} isNew flag for indicating new training track
 * @param {object} maintenanceInformation currently logged in user information
 * @returns training track creation/editing form
 */
const FormView = ({ trainingTrack, isNew, maintenanceInformation }) => {
	const [sSubmissionStatus, setSubmissionStatus] = useState(null);
	const history = useHistory();
	return (
		<>
			{trainingTrack && (
				<Formik
					initialValues={{
						...trainingTrack?.extractValues(),
					}}
					validationSchema={Yup.object().shape({
						[TRAINING_TRACK_KEYS.ID]: Yup.number()
							.integer('ID must be an integer')
							.typeError('ID must be a number')
							.positive('ID must be a positive number')
							.required('Training Track ID is required.')
							.test('Unique Key', async function (id) {
								if (!isNew) {
									return true;
								}
								if (id) {
									if (Number.isInteger(id)) {
										const result = await getTrainingTrack(
											id
										);
										if (
											result &&
											result?.trainingTrackID === id
										) {
											return this.createError({
												message: 'ID already in use.',
											});
										} else {
											return true;
										}
									} else {
										return this.createError({
											message:
												'Training Track ID must be an integer',
										});
									}
								} else {
									return this.createError({
										message:
											'Training Track ID is required.',
									});
								}
							}),
						[TRAINING_TRACK_KEYS.NAME]: Yup.string()
							.maxCounter(255)
							.required('Training Track Name is required.')
							.test('Unique Name', async function (value) {
								if (value) {
									const sanitizedValue = value
										.trim()
										.toLocaleLowerCase();
									const results = await searchTrainingTracks(
										sanitizedValue,
										true
									);
									if (results && results?.length > 0) {
										let exists = results.some(
											tt =>
												tt.label
													?.trim()
													.toLocaleLowerCase() ===
													sanitizedValue &&
												tt.id !==
													this.parent[
														TRAINING_TRACK_KEYS.ID
													]
										);
										if (exists) {
											return this.createError({
												message: 'Name already in use.',
											});
										} else {
											return true;
										}
									} else {
										return true;
									}
								} else {
									return this.createError({
										message:
											'Training Track Name is required.',
									});
								}
							}),
					})}
					validateOnMount
					onSubmit={async (values, actions) => {
						actions.setSubmitting(true);
						await submitTrainingTrackForm(
							{
								...new TrainingTrackDTO(values, isNew).prepare(
									maintenanceInformation,
									isNew
								),
							},
							isNew
						)
							.then(response => {
								actions.setSubmitting(false);
								if (response.data.payload === false) {
									let displayMessage =
										'Submitted Unsuccessfully';
									if (
										response?.data?.messages &&
										response.data.messages.length > 0 &&
										response.data.messages[0].Value
									) {
										displayMessage =
											response.data.messages[0].Value;
									}
									setSubmissionStatus({
										error: true,
										submitted: false,
										message: displayMessage,
									});
								} else {
									setSubmissionStatus({
										error: false,
										submitted: true,
										message: 'Submitted Successfully',
									});
								}
							})
							.catch(() => {
								actions.setSubmitting(false);
								setSubmissionStatus({
									error: true,
									submitted: false,
									message: 'Submission was unsuccessful',
								});
							});
						actions.setSubmitting(true);
					}}
				>
					{formik => (
						<Form>
							{sSubmissionStatus?.message && (
								<Alert
									severity={
										sSubmissionStatus?.error
											? 'error'
											: 'success'
									}
									variant='filled'
									onClose={() => {
										setSubmissionStatus(
											prev => (prev.message = '')
										);
									}}
								>
									{sSubmissionStatus?.message}
								</Alert>
							)}
							<Stack
								sx={{ marginTop: '32px' }}
								direction='column'
								spacing={{
									xs: 0.5,
									sm: 0.5,
									md: 1,
								}}
							>
								<>
									<FormControlLabel
										control={
											<Switch
												defaultChecked
												name={
													TRAINING_TRACK_KEYS.ACTIVE
												}
												id={`${BEM_NAME_ELEMENTS.VIEW_TRAINING_TRACK}__${BEM_NAME_ELEMENTS.FIELD_SWITCH}--${TRAINING_TRACK_KEYS.ACTIVE}`}
												checked={
													formik.values[
														TRAINING_TRACK_KEYS
															.ACTIVE
													]
												}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												disabled={
													sSubmissionStatus?.submitted
												}
											/>
										}
										label={
											formik.values[
												TRAINING_TRACK_KEYS.ACTIVE
											]
												? 'Active'
												: 'Inactive'
										}
									/>
									<FormHelperText
										error={
											!!formik.touched[
												TRAINING_TRACK_KEYS.ACTIVE
											] &&
											!!formik.errors[
												TRAINING_TRACK_KEYS.ACTIVE
											]
										}
									>
										{formik.touched[
											TRAINING_TRACK_KEYS.ACTIVE
										] &&
											formik.errors[
												TRAINING_TRACK_KEYS.ACTIVE
											]}
									</FormHelperText>
								</>
								<FormControlCustomized>
									<TextField
										name={TRAINING_TRACK_KEYS.ID}
										id={`${BEM_NAME_ELEMENTS.VIEW_TRAINING_TRACK}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${TRAINING_TRACK_KEYS.ID}`}
										label={
											<>
												<RequiredIndicator />
												Training Track ID
											</>
										}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[
												TRAINING_TRACK_KEYS.ID
											]
										}
										error={
											!!formik.touched[
												TRAINING_TRACK_KEYS.ID
											] &&
											!!formik.errors[
												TRAINING_TRACK_KEYS.ID
											]
										}
										helperText={
											formik.touched[
												TRAINING_TRACK_KEYS.ID
											] &&
											formik.errors[
												TRAINING_TRACK_KEYS.ID
											]
										}
										disabled={
											sSubmissionStatus?.submitted ||
											!isNew
										}
									/>
								</FormControlCustomized>
								<FormControlCustomized>
									<TextField
										name={TRAINING_TRACK_KEYS.NAME}
										id={`${BEM_NAME_ELEMENTS.VIEW_TRAINING_TRACK}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${TRAINING_TRACK_KEYS.NAME}`}
										label={
											<>
												<RequiredIndicator />
												Name{' '}
											</>
										}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values[
												TRAINING_TRACK_KEYS.NAME
											]
										}
										error={
											!!formik.touched[
												TRAINING_TRACK_KEYS.NAME
											] &&
											!!formik.errors[
												TRAINING_TRACK_KEYS.NAME
											]
										}
										helperText={
											formik.touched[
												TRAINING_TRACK_KEYS.NAME
											] &&
											formik.errors[
												TRAINING_TRACK_KEYS.NAME
											]
										}
										disabled={sSubmissionStatus?.submitted}
									/>
								</FormControlCustomized>
								<Button
									id={`${BEM_NAME_ELEMENTS.VIEW_TRAINING_TRACK}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--new`}
									type='submit'
									variant={'contained'}
									disabled={
										(!!formik.errors &&
											Object.keys(formik.errors).length >
												0) ||
										!formik.dirty ||
										formik.isSubmitting ||
										sSubmissionStatus?.submitted
									}
								>
									{formik.isSubmitting
										? 'Submitting...'
										: sSubmissionStatus?.submitted
										? 'Submitted'
										: isNew
										? 'Create'
										: 'Save'}
								</Button>
								{sSubmissionStatus?.submitted && (
									<>
										{isNew && (
											<Button
												id={`${BEM_NAME_ELEMENTS.VIEW_TRAINING_TRACK}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--new`}
												variant={'contained'}
												color={'success'}
												onClick={() => {
													formik.resetForm({
														...trainingTrack.extractValues(),
													});
													setSubmissionStatus(null);
												}}
											>
												Submit New Training Track
											</Button>
										)}
										<Button
											id={`${BEM_NAME_ELEMENTS.VIEW_TRAINING_TRACK}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--home`}
											variant={'contained'}
											color={'primary'}
											onClick={() =>
												history.push(
													'/EpicUtilities/Home'
												)
											}
										>
											Back to Epic Administration Home
										</Button>
									</>
								)}
							</Stack>
						</Form>
					)}
				</Formik>
			)}
		</>
	);
};
export default FormView;
